import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import DefaultButton from "../general/buttons/DefaultButton";
import { SyncSongDTO } from "../../services/sync";
import { buildManualRemovalsCsv, downloadTextFile, slugifyForFilename } from "../../services/csvExport";
import { colors } from "../../theme";

interface ManualRemovalDialogProps {
    open: boolean;
    provider: string;
    playlistName: string;
    songs: SyncSongDTO[];
    onClose: () => void;
}

const SHORTCUT_STEPS = [
    "Open the Shortcuts app and tap the + to start a new shortcut.",
    "Add “Choose File” (or “Get File”) so it asks you which file to use each run.",
    "Add “Get Text from Input”, fed by the chosen file.",
    "Add “Split Text”, splitting by “New Lines” — this turns the CSV into one item per row.",
    "Add “Repeat with Each” over that list.",
    "Inside the repeat: add another “Split Text”, splitting the current row by “,” (comma) — item 1 is Title, item 2 is Artist.",
    "Still inside the repeat: add “Find Music”, filtered to your library (or the specific playlist), matching Title = Split Text item 1 and Artist = Split Text item 2.",
    "Add “Quick Look” (or “Show Result”) on what Find Music returns — this pulls the matching song up so you can remove it with a swipe or the ‘…’ menu in the Music app.",
    "Save the shortcut (e.g. “Shufl Removal Helper”). Run it each time, choosing the freshly downloaded CSV.",
];

/**
 * Shown after a commit whose provider can't remove tracks itself (Apple Music has no
 * removal endpoint - see MusicProviderService.supportsRemoval()). Explains what the
 * CSV is for before offering it, so the download is something the user asked for
 * rather than a file appearing unannounced.
 */
export default function ManualRemovalDialog(props: ManualRemovalDialogProps) {
    const { open, provider, playlistName, songs, onClose } = props;
    const [downloaded, setDownloaded] = useState(false);
    const filename = `${slugifyForFilename(playlistName)}-${slugifyForFilename(provider)}-removals.csv`;

    const handleDownload = () => {
        downloadTextFile(filename, buildManualRemovalsCsv(songs, provider));
        setDownloaded(true);
    };

    const handleClose = () => {
        setDownloaded(false);
        onClose();
    };

    if (songs.length === 0) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: colors.elevated,
                        border: `1px solid ${colors.line}`,
                        borderRadius: 3,
                    },
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {songs.length} song{songs.length === 1 ? "" : "s"} need manual removal
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: colors.textSecondary, fontSize: 14, mb: 2 }}>
                    {provider}'s API has no way for an app to remove a track from your library
                    playlist — only the Music app itself can do that (Apple confirms this is
                    intentional, not a gap in Shufl). Everything else in this sync already applied;
                    these {songs.length === 1 ? "is" : "are"} the only one{songs.length === 1 ? "" : "s"} left.
                </Typography>
                <Typography sx={{ color: colors.textSecondary, fontSize: 14, mb: 2 }}>
                    Download a CSV of exactly these songs and you (or a Shortcut) can remove them
                    quickly, instead of hunting through the playlist by hand.
                </Typography>

                <Box
                    sx={{
                        borderRadius: 2,
                        border: `1px solid ${colors.line}`,
                        backgroundColor: colors.base,
                        maxHeight: 220,
                        overflowY: "auto",
                        mb: 2,
                    }}
                >
                    <List dense disablePadding>
                        {songs.map(song => (
                            <ListItem key={song.id} sx={{ borderBottom: `1px solid ${colors.line}` }}>
                                <ListItemText
                                    primary={song.title}
                                    secondary={song.artist}
                                    slotProps={{
                                        primary: { fontSize: 14, fontWeight: 600 },
                                        secondary: { fontSize: 12, color: colors.textMuted },
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                {downloaded && (
                    <Typography sx={{ color: colors.success, fontSize: 13, mb: 1 }}>
                        Downloaded as {filename}.
                    </Typography>
                )}

                <Accordion
                    disableGutters
                    sx={{
                        backgroundColor: "transparent",
                        border: `1px solid ${colors.line}`,
                        borderRadius: 2,
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.textSecondary }} />}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                            Don't have the removal Shortcut yet? Build it once (~2 min)
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography sx={{ fontSize: 12, color: colors.textMuted, mb: 1.5 }}>
                            This is a Shortcuts-app recipe, not an automatic install — Apple requires
                            shortcuts to be built (or explicitly trusted) on your own device, so there's
                            no file Shufl can hand you that just works. Once you've built it, it's yours
                            to reuse and share.
                        </Typography>
                        <List dense disablePadding>
                            {SHORTCUT_STEPS.map((step, i) => (
                                <ListItem key={i} sx={{ py: 0.5, alignItems: "flex-start" }}>
                                    <Typography sx={{ fontSize: 12, color: colors.accent, fontWeight: 700, mr: 1, minWidth: 16 }}>
                                        {i + 1}.
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                                        {step}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                        <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 1.5, fontStyle: "italic" }}>
                            Note: this naive comma-split misreads a row if a title or artist itself
                            contains a comma — open the CSV in Numbers/Excel first if you're unsure,
                            and handle those few by hand.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <DefaultButton text="Close" variant="secondary" onClick={handleClose} />
                <DefaultButton text={downloaded ? "Download again" : "Download CSV"} onClick={handleDownload} />
            </DialogActions>
        </Dialog>
    );
}
